import {
  calculateCostPerShift,
  calculateRvusPerShift,
  calculateCostPerHour as calculateSharedCostPerHour,
  calculatePricePerRVU as calculateSharedPricePerRVU,
  resolveDiffType,
} from '~/features/market/listings/components/costCalculatorMoney';
import { ShiftDifferentialType } from '~/types/hlx/enums';
import { JobListingDetail } from '~/types/hlx/jobListingDetail';

export { calculateCostPerShift, calculateRvusPerShift };

/**
 * [MCV-1955] What the calculator reads from a listing. The provider-facing marketplace payload
 * is a partial JobListingDetail, so only a persisted `id` is required; every other field is
 * optional and missing values calculate as 0.
 */
export type JobCostDetail = Pick<JobListingDetail, 'id'> & Partial<Omit<JobListingDetail, 'id'>>;

const SHIFT_TYPE_ID_MAP: Record<string, number> = {
  Day: 0,
  Evening: 1,
  Night: 2,
  'Weekday Day': 3,
  'Weekday Evening': 4,
  'Weekday Night': 5,
  'Weekend Day': 6,
  'Weekend Evening': 7,
  'Weekend Night': 8,
  'Holiday Day': 9,
  'Holiday Evening': 10,
  'Holiday Night': 11,
};

const SHIFT_COUNT_KEYS = [
  'numDayShifts',
  'numEveShifts',
  'numNightShifts',
  'numOfWeDayShifts',
  'numOfWeEveShifts',
  'numOfWeNightShifts',
  'numHolDayShifts',
  'numHolEveShifts',
  'numHolNightShifts',
  'numDayCallShifts',
  'numEveCallShifts',
  'numNightCallShifts',
  'numOfWeDayCallShifts',
  'numOfWeEveCallShifts',
  'numOfWeNightCallShifts',
  'numHolDayCallShifts',
  'numHolEveCallShifts',
  'numHolNightCallShifts',
] satisfies (keyof JobListingDetail)[];

export const calculateDifferential = (
  detail: JobCostDetail,
  rowData: { diffKey: keyof JobListingDetail }
): number => Number(detail[rowData.diffKey] ?? 0);

export const calculateCostPerHour = (
  detail: JobCostDetail,
  differential: number,
  diffType?: ShiftDifferentialType | null
): number => calculateSharedCostPerHour(detail, differential, diffType);

export const calculatePricePerRVU = (
  detail: JobCostDetail,
  differential: number,
  diffType?: ShiftDifferentialType | null,
  hourlyRate?: number
): number => calculateSharedPricePerRVU(detail, differential, diffType, hourlyRate);

export type RowDataConfig = {
  diffKey: keyof JobListingDetail;
  countKey: keyof JobListingDetail;
  shiftType: string;
  tableIdx: number;
  isCallShift?: boolean;
};

export const generateRowData = (detail: JobCostDetail, rowData: RowDataConfig) => {
  const diffType = resolveDiffType(
    rowData.isCallShift ? detail.callShiftDiffType : detail.shiftDiffType
  );
  const shiftCount = Number(detail[rowData.countKey] ?? 0);
  const differential = calculateDifferential(detail, rowData);
  const hourlyRate = calculateCostPerHour(detail, differential, diffType);
  const costPerShift = calculateCostPerShift(detail, hourlyRate);

  return {
    id: detail.id + rowData.tableIdx * 10 + (SHIFT_TYPE_ID_MAP[rowData.shiftType] ?? 0),
    shiftType: rowData.shiftType,
    count: shiftCount,
    baseRate: hourlyRate,
    differential,
    totalRate: Math.round(hourlyRate * 100) / 100,
    hoursPerShift: detail.hoursPerShift,
    costPerShift,
    subTotal: shiftCount * costPerShift,
    rvus: shiftCount * calculateRvusPerShift(detail),
    hourlyRate,
    pricePerRvu: calculatePricePerRVU(detail, differential, diffType, hourlyRate),
  };
};

export const checkIfShiftDetails = (detail: JobCostDetail): boolean =>
  SHIFT_COUNT_KEYS.some((key) => Number(detail[key] ?? 0) > 0);
